import React, { useEffect, useState } from 'react';
import CustomTable from '../../components/Tables/CustomTable';
import ToastManager from '../../components/Toast/ToastManager';
import SaveButton from '../../components/Buttons/SaveButton';
import api from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../hooks/useToast';
import SearchBar from '../../components/SearchBar/SearchBar';
import EntregaKitsLoteModal from '../../components/Modals/EntregaKitsLoteModal';
import { 
  PageContainer, 
  PageTitle, 
  CheckboxCell,
  SelectedCountText
} from './RegistrarEntregaKitsLotePage.styles';

const headerContainer = {
  marginBottom: '30px'
};

const topControlsContainer = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '15px',
  marginBottom: '20px'
};

const pageSizeContainer = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px'
};

const pageSizeSelect = {
  padding: '8px 12px',
  borderRadius: '4px',
  border: '1px solid #ccc',
  fontSize: '14px',
  cursor: 'pointer',
  backgroundColor: '#fff'
};

const RegistrarEntregaKitsLotePage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [selectedGestantes, setSelectedGestantes] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [pageSize, setPageSize] = useState(25);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const { toasts, removeToast, showSuccess, showError } = useToast();

  const fetchData = async (page = 1, size = pageSize, searchTerm = "") => {
    setLoading(true);
    setError(null);

    try {
      const params = { 
        page, 
        page_size: size 
      };

      if (searchTerm && searchTerm.trim() !== "") {
        params.search = searchTerm.trim();
      }

      const response = await api.get('/gestante/kits/elegaveis', { params });

      const { data: gestantesData, pagination } = response.data;
      
      setData(gestantesData);
      setCurrentPage(pagination.current_page);
      setHasNextPage(pagination.has_next);
      setSelectedGestantes([]);
      setSelectAll(false);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        setError('Usuário não autorizado. Por favor, faça login.');
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } else {
        console.error('Erro ao carregar os dados:', error);
        setError('Erro ao carregar os dados');
        showError('Erro ao carregar gestantes elegíveis');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectGestante = (gestanteCpf) => {
    setSelectedGestantes((prev) => {
      if (prev.includes(gestanteCpf)) {
        return prev.filter((cpf) => cpf !== gestanteCpf);
      } else {
        return [...prev, gestanteCpf];
      }
    });
  };

  // Função para selecionar/desselecionar todos
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedGestantes([]);
    } else {
      setSelectedGestantes(data.map((gestante) => gestante.gestante_cpf));
    }
    setSelectAll(!selectAll);
  };

  const formatCPF = (cpf) => {
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };


  const handleEntregarKitsLote = () => {
    if (selectedGestantes.length === 0) {
      showError('Selecione pelo menos uma gestante');
      return;
    }
    setIsModalOpen(true);
  };

  const handleConfirmarEntrega = async (selectedInModal) => {
    try {
      const gestantesSelecionadas = data.filter(gestante => 
        selectedInModal.includes(gestante.gestante_cpf)
      );

      const today = new Date().toISOString().split('T')[0]; // Formato YYYY-MM-DD
      
      const payload = {
        data_entrega_kits: today,
        entregas: gestantesSelecionadas.map(gestante => ({
          gravidez_id: gestante.gravidez_id,
          quantidade_kits: gestante.qtd_filhos
        }))
      };

      const response = await api.put('/gestante/gravidez/entrega-kit/lote', payload);
      const { sucessos, erros } = response.data;

      setIsModalOpen(false);

      if (sucessos > 0) {
        showSuccess(`${sucessos} kit(s) encaminhado(s) com sucesso.`);
      }

      if (erros > 0) {
        showError(`${erros} erro(s) ao registrar a(s) entrega(s).`);
      }

      setSelectedGestantes([]);
      setSelectAll(false);
      fetchData(currentPage, pageSize);
    } catch (error) {
      console.error('Erro ao registrar entrega de kits:', error);
      setIsModalOpen(false);
      showError('Erro ao registrar entrega de kits');
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handlePageSizeChange = (e) => {
    const newSize = parseInt(e.target.value);
    setPageSize(newSize);
    setCurrentPage(1);
    fetchData(1, newSize);
  };

  return (
    <PageContainer>
      <div style={headerContainer}>
        <PageTitle style={{ textAlign: 'center' }}>Registrar Kits em Lotes</PageTitle>
      </div>

      {loading ? (
        <p>Carregando...</p>
      ) : error ? (
        <p style={{ color: '#1a1a1a' }}>{error}</p>
      ) : (
        <>
          <div style={topControlsContainer}>
            <SaveButton
              label={'Entregar kits em lote'}
              onClick={handleEntregarKitsLote}
              disabled={selectedGestantes.length === 0}
            />
            <div style={pageSizeContainer}>
              <label htmlFor="pageSize" style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>Itens por página:</label>
              <select
                id="pageSize"
                value={pageSize}
                onChange={handlePageSizeChange}
                style={pageSizeSelect}
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>

          <SearchBar
            placeholder={'Buscar por CPF, nome ou Município...'}
            onSearch={(term) => {
              setSearchTerm(term);
              fetchData(1, pageSize, term);
            }}
          />

          {selectedGestantes.length > 0 && (
            <SelectedCountText>
              {selectedGestantes.length} gestante(s) selecionada(s)
            </SelectedCountText>
          )}

          <CustomTable
            columns={[
              { 
                field: 'checkbox', 
                headerName: (
                  <CheckboxCell>
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={handleSelectAll}
                    />
                  </CheckboxCell>
                )
              },
              { field: 'cpf_formatado', headerName: 'CPF' },
              { field: 'gestante_nome', headerName: 'Nome da Gestante' },
              { field: 'municipio_nome', headerName: 'Nome Município' },
              { field: 'status_gestante', headerName: 'Status da Gestante' },
              { field: 'qtd_filhos', headerName: 'Número de Filhos' },
              { field: 'numero_kits', headerName: 'Número de Kits' },
            ]}
            data={data.map((gestante) => ({
              ...gestante,
              checkbox: (
                <CheckboxCell>
                  <input
                    type="checkbox"
                    checked={selectedGestantes.includes(gestante.gestante_cpf)}
                    onChange={() => handleSelectGestante(gestante.gestante_cpf)}
                  />
                </CheckboxCell>
              ),
              cpf_formatado: formatCPF(gestante.gestante_cpf),
              status_gestante: gestante.status_gravidez ? 'Ativa' : 'Inativa',
              numero_kits: gestante.qtd_filhos,
            }))}
            currentPage={currentPage}
            hasNextPage={hasNextPage}
            onPageChange={(page) => fetchData(page, pageSize, searchTerm)}
          />
        </>
      )}

      {/* Toast Manager para notificações */}
      <ToastManager toasts={toasts} removeToast={removeToast} />

      {/* Modal de confirmação de entrega em lote */}
      <EntregaKitsLoteModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        selectedGestantes={selectedGestantes}
        gestantesData={data}
        onConfirm={handleConfirmarEntrega}
      />
    </PageContainer>
  );
};

export default RegistrarEntregaKitsLotePage;
